import type { SurveySection } from "@survey-types/survey.section.types";
import { createElement } from "react";

// Component imports
import LeaseComparisonSection from "@/features/survey/components/sections/LeaseComparisonSection";
import PropertyComparisonSection from "@survey-comparison/PropertyComparisonSection";
import CoverSection from "@survey-cover/CoverSection";
import CustomSection from "@survey-sections/CustomSection";
import MarketMapSection from "@survey-sections/MarketMapSection";
import PropertiesSection from "@survey-sections/PropertiesSection";
import TourScheduleSection from "@survey-tour/TourScheduleSection";

// Component mapping based on section names - using modern React 19 createElement
export const sectionComponentMap = {
  cover: () => createElement(CoverSection),
  "market map": () => createElement(MarketMapSection),
  "property comparison": () => createElement(PropertyComparisonSection),
  "lease comps": () => createElement(LeaseComparisonSection),
  properties: () => createElement(PropertiesSection),
  "tour schedule": () => createElement(TourScheduleSection),
} as const;

/**
 * Maps section names to their display names for the UI
 * This allows us to show different names in the UI than what's stored in the database
 */
export const getSectionDisplayName = (sectionName: string): string => {
  const normalizedName = sectionName.toLowerCase().trim();

  const displayNameMap: Record<string, string> = {
    "lease comps": "Lease Comps",
    "property comparison": "Property Comparison",
    "market map": "Market Map",
    "tour schedule": "Tour Schedule",
    cover: "Cover",
    properties: "Properties",
  };

  return displayNameMap[normalizedName] || sectionName;
};

// Function to get component based on section name
export const getSectionComponent = (
  sectionName: string,
  section?: SurveySection,
) => {
  const normalizedName = sectionName.toLowerCase().replace(/[_-]/g, " ");

  // Show CustomSection for all custom sections regardless of name
  if (section?.isCustom) {
    return createElement(CustomSection, {
      sectionId: section.surveySectionId,
      section: section,
    });
  }

  const componentFactory =
    sectionComponentMap[normalizedName as keyof typeof sectionComponentMap];
  return componentFactory ? componentFactory() : null;
};
